<template>
  <div ref="containerRef"
       class="options"
       :class="[{
        'options--grouped': isGrouped,
        'options--inline': inline,
        'options--multiple': multiple,
        'options--single': !multiple,
        'options--grid': grid,
       },`options--grid-${grid}`, optionsClass]"
  >
    <Scrollbar ref="scrollbarRef" @scroll-start="onScrollStart" @scroll="onScroll" @scroll-end="onScrollEnd">
      <!-- loader -->
      <Placeholders
          v-if="loading && !initialLoad"
          class="py-5"
      />

      <!-- content -->
      <div v-else
           class="options__content"
      >
        <!-- Empty list -->
        <div v-if="!visibleOptions.length"
             class="options__empty"
        >
          <slot name="emptyList">
            <template v-if="autocomplete">
              {{ request.search ? $t('front.no_data_found') : $t('front.enter_fragment_of') }}
            </template>
            <template v-else>
              {{ $t('front.no_data_found') }}
            </template>
          </slot>
        </div>

        <!-- Not empty -->
        <template v-else>
          <!-- Grouped options -->
          <SelectOptionGroup
              v-if="isGrouped"
              v-for="group in visibleOptions"
              :key="group[groupValueKey]"
              :group="group"
              :value-key="valueKey"
              :group-title-key="groupTitleKey"
              :option-props="optionProps"
              :multiple="multiple"
              @select-group="selectGroup"
              @select="select"
              @group-toggle="emit('groupToggle', group)"
          />

          <SelectOption
              v-else
              v-for="option in visibleOptions"
              :key="option[valueKey]"
              :option="option"
              v-bind="optionProps"
              @select="select"
          >
            <slot name="option" v-bind="{option}" />
          </SelectOption>
        </template>

        <Spinner v-if="loading" class="py-2" />
      </div>
    </Scrollbar>

    <div v-if="showPagination"
         class="options__pagination"
         :class="{'options__pagination--shadow': bottomShadow}"
    >
      <Pagination
          v-model="request.page"
          v-model:per-page="request.perPage"
          :disabled="loading"
          :total-pages="request.lastPage"
          :disable-go-to-page="!showGoToPage"
          :disable-items-per-page="!showPerPage"
          :per-page-list="request.showPerPage"
          @change="onPageChange"
      />
    </div>
  </div>
</template>
<script setup>
import FormControlProps from "@/js/Elements/Form/FormControlProps.js";
import Scrollbar from "@/js/Elements/Scrollbar.vue";
import Placeholders from "@/js/Elements/Placeholders.vue";
import SelectOption from "@/js/Elements/Select/SelectOption.vue";
import {computed, nextTick, onMounted, ref} from "vue";
import Spinner from "@/js/Elements/Spinner.vue";
import Pagination from "@/js/Elements/Pagination.vue";
import SelectOptionGroup from "@/js/Elements/Select/SelectOptionGroup.vue";

const emit = defineEmits(['scrollEnd', 'scrollStart', 'scroll', 'select', 'pageChange', 'groupToggle']);
const props = defineProps({
  ...FormControlProps,
  options: Array,
  loading: Boolean,
  initialLoad: Boolean,
  request: Object,
  isGrouped: Boolean,
  searchFilter: Function,
  showPagination: Boolean,
  showGoToPage: Boolean,
  showPerPage: Boolean,
  optionsClass: [String, Array, Object]
});
const containerRef = ref();
const scrollbarRef = ref();
const bottomShadow = ref(false);
const optionProps = computed(() => {
  const p = {};
  Object.keys(FormControlProps).forEach(key => {
    p[key] = props[key];
  });
  return p;
});
const visibleOptions = computed(() => props.options ? props.options.filter(option => option._visible_) : []);

defineExpose({
  getContainer: () => containerRef.value,
  isScrollable,
});

onMounted(() => {
  bottomShadow.value = scrollbarRef.value.isScrollable();
});

function isScrollable() {
  return scrollbarRef.value && scrollbarRef.value.isScrollable();
}

function select(option) {
  emit('select', option);
}

function selectGroup(group) {
  emit('selectGroup', group);
}

function onScrollStart() {
  bottomShadow.value = true;
  emit('scrollStart');
}

function onScroll() {
  bottomShadow.value = true;
  emit('scroll');
}

function onScrollEnd() {
  nextTick(() => {
    bottomShadow.value = false;
  });
  if (props.showPagination) return;
  emit('scrollEnd', isScrollable());
}

function onPageChange(value) {
  if (!value) return;
  emit('pageChange', value);
}
</script>
<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";
@import "bootstrap/scss/functions";
@import "bootstrap/scss/mixins";
@import "bootstrap/scss/variables";

.options {
  :deep(.scrollbar) {
    height: unset;
  }

  &:not(.options--inline) &:deep(.ps) {
    max-height: toRem(300);
    overflow-y: auto;
  }

  &__content {
    max-height: toRem(300);
  }

  &__empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: spacing(3);
    color: themeColor('neutral-500');
  }

  &:deep(.option) {
    display: flex;
    width: 100%;
  }

  &__pagination {
    display: flex;
    justify-content: flex-end;
    padding: spacing(2);

    &--shadow {
      box-shadow: 0 toRem(-1) toRem(4) rgba(0, 0, 0, 0.075);
      position: relative;
      z-index: 2;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    padding: spacing(2);
    gap: spacing(1);
  }

  &:deep(.option__content) {
    width: 100%;
    display: flex;
    align-items: center;
    padding: spacing(1) spacing(3);
    cursor: pointer;
    font-size: fontSize(5);
    min-height: toRem(35);
    font-weight: $font-weight-medium;
    border-radius: $border-radius-lg;

    &:hover {
      background-color: themeColor('neutral-100');
    }
  }

  //&:deep(.option.selected .option__content) {
  //  background-color: themeColor('neutral-100');
  //}

  &--grouped {
    .options-group {
      width: 100%;
      border: themeColor('border') solid toRem(1);
      border-radius: $border-radius-lg;
    }
  }

  &--single,
  &--multiple {
    > .ps {
      margin-bottom: spacing(1);
    }
  }

  &--single {
    &:deep(.option) {
      .option__content {
        border: transparent solid toRem(1);
        .option__check {
          margin-right: spacing(2);
        }
      }

      &.selected .option__content {
        cursor: default;
        background-color: themeColor('neutral-50');
        border-color: themeColor('primary');
        .option__check { color: themeColor('primary'); }
      }
    }
  }

  &--grid {
    :deep(.options-group__content),
    :deep(.options__content) {
      flex-direction: row;
      flex-wrap: wrap;
      gap: spacing(2);
      padding-bottom: spacing(2);
    }

    &:deep(.option),
    &.options--grid-3 :deep(.option),
    &.options--grid-2 :deep(.option) {
      width: calc(100% / 2 - #{spacing(1)});
    }

    &.options--grid-8 :deep(.option) {
      width: calc(100% / 2 - #{spacing(2)});
      @include media-breakpoint-up(sm) {
        width: calc(100% / 8 - #{spacing(2)});
      }
    }
  }

  &--inline {
    padding-bottom: spacing(2);

    .options__content {
      padding-top: spacing(2);
      padding-bottom: 0;
    }

    &:deep(.option__content) {
      padding: spacing(2);
      //border: themeColor('border') solid toRem(1);
      //border-radius: $border-radius-lg;
    }

    &.options--grouped {
      .options__content {
        width: 100%;
        gap: spacing(2);
      }
    }
  }
}
</style>